// ── Workflow Builder stage ──────────────────────────────────────────────────
//
// Embeds the Ops Center Mockup config surfaces as a Platform Journey stage.
// Lists v2 workflows (open the visual node editor) and v1 recipes (open the
// entity_profiles recipe editor). Reuses the Ops Center store so both tabs
// share the same seeded data.
import { useEffect, useRef, useState, type ChangeEvent } from 'react'
import { Plus, Upload, ArrowLeft, FileText, Trash2, Check, X, Sparkles } from 'lucide-react'
import { useOpsCenterStore } from '@features/dashboards/components/OpsCenterMockup/store'
import { WorkflowEditor } from '@features/dashboards/components/OpsCenterMockup/components/WorkflowEditor'
import { WorkflowPreviewStrip } from '@features/dashboards/components/OpsCenterMockup/components/WorkflowPreviewStrip'
import { RecipeEditor } from '@features/dashboards/components/OpsCenterMockup/components/RecipeEditor'
import { importWorkflowJson } from '@features/dashboards/components/OpsCenterMockup/io'
import type { Workflow } from '@features/dashboards/components/OpsCenterMockup/types'
import { StageCompleteNav } from './StageCompleteNav'
import { ExperienceModeSwitcher } from './ExperienceModeSwitcher'
import type { ExperienceMode } from './ExperienceModeSwitcher'
import { WorkflowBuilderGuidedView } from './WorkflowBuilderGuidedView'
import { WorkflowBuilderExistingCustomerView, V1RecipeVisual } from './WorkflowBuilderExistingCustomerView'
import { StageSaveBar } from './StageSaveBar'
import type { ExistingCustomerSelection } from './ExistingCustomerPanel'
import type { CustomerMode } from './NewExistingToggle'
import { platformJourneysApi } from '@api/client'
import type { PlatformJourneyRecord } from '@api/client'
import { V1_RECIPES } from '@features/scope/types'
import { useAiSeed } from '../aiConfigure'
import type { AiConfigureSuggestions, AiStageSuggestionState, AiWorkflowSuggestion } from '../aiConfigure'
import { AiSuggestionBanner } from './AiSuggestionBanner'
import { WorkflowGraphPreview } from './WorkflowGraphPreview'
import { buildWorkflowTasksFromAcceptedStages, hasAnyAcceptedWorkflowStage } from '../workflowGraphFromSuggestions'
import type { AcceptedWorkflowStages } from '../workflowGraphFromSuggestions'

// A saved journey's workflowConfig is either a real OpsCenterMockup v2
// Workflow, or this lightweight marker for "the rep applied an AI-recommended
// v1 recipe" — both are opaque JSON on the wire (see PlatformJourneyRecord),
// so this shape only needs to exist here, where it's written and read back.
interface V1RecipeWorkflowConfig {
  kind: 'v1Recipe'
  recipeKey: string
}
function isV1RecipeConfig(v: unknown): v is V1RecipeWorkflowConfig {
  return !!v && typeof v === 'object' && (v as { kind?: unknown }).kind === 'v1Recipe'
}

const DEMO_ACCOUNT_ID = 'acc_demo'

export function newEmptyWorkflow(accountId: string, name: string): Workflow {
  const now = new Date().toISOString()
  return {
    id: `wf_${Math.random().toString(36).slice(2, 9)}`,
    accountId,
    name: name || 'New Workflow',
    description: '',
    version: 1,
    tags: [],
    variables: [],
    tasks: [],
    webhook: { url: '', headers: {}, events: ['workflow.completed'], retryCount: 3 },
    configFiles: {},
    createdAt: now,
    updatedAt: now,
  }
}

interface WorkflowBuilderStageProps {
  /** Label of the next stage to move on to, or null if this is the last visible stage. */
  nextStageLabel?: string | null
  /** Advances to the next stage — omitted/undefined when there isn't one. */
  onAdvance?: () => void
  /** Shared across every stage. */
  experienceMode: ExperienceMode
  onExperienceModeChange: (mode: ExperienceMode) => void
  customerMode: CustomerMode
  existingCustomer: ExistingCustomerSelection | null
  journeyId: number | null
  journeyRecord: PlatformJourneyRecord | null
  onJourneySaved: () => void
  aiSuggestions?: AiConfigureSuggestions | null
  /** Lifted to the tool shell (rather than computed here) so Account Setup can
   * also show a confirm/reject widget for the same recommendation right where
   * it was generated — both views share this one apply/dismiss state. */
  workflowAi: AiStageSuggestionState<AiWorkflowSuggestion>
  /** Same lifted per-stage state Account Setup's checklist uses — read here
   * only for `.applied`, to build the workflow graph out of whatever the rep
   * has actually accepted so far (see workflowGraphFromSuggestions.ts). */
  idvAi: AiStageSuggestionState<unknown>
  cascadeAi: AiStageSuggestionState<unknown>
  duplicatesAi: AiStageSuggestionState<unknown>
  kybAi: AiStageSuggestionState<unknown>
  amlAi: AiStageSuggestionState<unknown>
  fraudAi: AiStageSuggestionState<unknown>
  tmAi: AiStageSuggestionState<unknown>
}

export function WorkflowBuilderStage({
  nextStageLabel, onAdvance, experienceMode, onExperienceModeChange, customerMode, existingCustomer,
  journeyId, journeyRecord, onJourneySaved, aiSuggestions, workflowAi,
  idvAi, cascadeAi, duplicatesAi, kybAi, amlAi, fraudAi, tmAi,
}: WorkflowBuilderStageProps) {
  const mode = experienceMode
  const acceptedStages: AcceptedWorkflowStages = {
    duplicates: duplicatesAi.applied,
    cascade: cascadeAi.applied,
    idv: idvAi.applied,
    aml: amlAi.applied,
    kyb: kybAi.applied,
    fraud: fraudAi.applied,
    tm: tmAi.applied,
  }
  const workflows = useOpsCenterStore((s) => s.workflows)
  const recipes = useOpsCenterStore((s) => s.recipes)
  const accounts = useOpsCenterStore((s) => s.accounts)
  const selectedWorkflowId = useOpsCenterStore((s) => s.selectedWorkflowId)
  const selectedRecipeId = useOpsCenterStore((s) => s.selectedRecipeId)
  const selectWorkflow = useOpsCenterStore((s) => s.selectWorkflow)
  const selectRecipe = useOpsCenterStore((s) => s.selectRecipe)
  const addWorkflow = useOpsCenterStore((s) => s.addWorkflow)
  const deleteWorkflow = useOpsCenterStore((s) => s.deleteWorkflow)

  const [appliedRecipeKey, setAppliedRecipeKey] = useState<string | null>(null)

  // Always start on the list when the stage opens.
  useEffect(() => {
    selectWorkflow(null)
    selectRecipe(null)
    return () => { selectWorkflow(null); selectRecipe(null) }
  }, [selectWorkflow, selectRecipe])

  // Hydrate the saved workflow snapshot into the shared Ops Center store once
  // per distinct loaded journey — a brand-new journey has no workflowConfig yet
  // so this is a no-op until the rep saves one. A saved config is either a real
  // v2 Workflow, or the lightweight "applied this AI-recommended v1 recipe"
  // marker — the two are mutually exclusive, so only one branch ever fires.
  useEffect(() => {
    const cfg = journeyRecord?.workflowConfig
    if (isV1RecipeConfig(cfg)) {
      setAppliedRecipeKey(cfg.recipeKey)
      return
    }
    setAppliedRecipeKey(null)
    const wf = cfg as Workflow | null | undefined
    if (!wf) return
    if (!workflows.some((w) => w.id === wf.id)) addWorkflow(accounts[0]?.id ?? DEMO_ACCOUNT_ID, wf)
    selectWorkflow(wf.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyRecord?.id])

  const [newName, setNewName] = useState('')
  const [importError, setImportError] = useState<string | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const accountId = accounts.find((a) => a.id === DEMO_ACCOUNT_ID)?.id ?? accounts[0]?.id ?? DEMO_ACCOUNT_ID

  // Picking a workflow to use — whether from the list, newly created, or imported —
  // persists it against the journey immediately, the same way every other stage's
  // config is saved. Without this, the selection only lived in the Ops Center store
  // and was lost on reload unless the rep remembered to also click "Save
  // configuration" inside the editor.
  async function persistWorkflowSelection(workflow: Workflow) {
    if (!journeyId) return
    const res = await platformJourneysApi.update(journeyId, { workflowConfig: workflow })
    if (res.success) onJourneySaved()
  }

  // Applying the AI's recommended recipe is the v1-recipe equivalent of
  // persistWorkflowSelection — it's a distinct, opaque workflowConfig shape
  // (see V1RecipeWorkflowConfig above), not a real Workflow the Ops Center
  // store or editor knows how to open.
  async function applyRecipeKey(recipeKey: string) {
    setAppliedRecipeKey(recipeKey)
    if (!journeyId) return
    const res = await platformJourneysApi.update(journeyId, { workflowConfig: { kind: 'v1Recipe', recipeKey } })
    if (res.success) onJourneySaved()
  }

  useAiSeed(workflowAi.seed, (v) => { if (v.recipeKey) void applyRecipeKey(v.recipeKey) })

  function handleSelectWorkflow(workflow: Workflow) {
    selectWorkflow(workflow.id)
    void persistWorkflowSelection(workflow)
  }

  // The guided flow already added the workflow to the store by the time this fires,
  // so reading straight from getState() avoids relying on a possibly-stale `workflows`
  // closure from this component's last render.
  function handleWorkflowCreatedFromGuided(workflowId: string) {
    selectWorkflow(workflowId)
    const wf = useOpsCenterStore.getState().workflows.find((w) => w.id === workflowId)
    if (wf) void persistWorkflowSelection(wf)
  }

  function handleNew() {
    const wf = newEmptyWorkflow(accountId, newName.trim())
    addWorkflow(accountId, wf)
    setNewName('')
    handleSelectWorkflow(wf)
  }

  async function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    try {
      const parsed: unknown = JSON.parse(await file.text())
      const wf = importWorkflowJson(parsed, accountId)
      addWorkflow(accountId, wf)
      setImportError(null)
      handleSelectWorkflow(wf)
    } catch (err) {
      setImportError(err instanceof Error ? `Import failed: ${err.message}` : 'Import failed: invalid JSON')
    }
  }

  // ── Existing customer — show exactly what's configured for them, read-only ──
  if (customerMode === 'existing') {
    return (
      <WorkflowBuilderExistingCustomerView
        existingCustomer={existingCustomer}
        aiSuggestions={aiSuggestions}
        acceptedStages={acceptedStages}
      />
    )
  }

  // ── Workflow editor view (v2) ──
  if (selectedWorkflowId) {
    return (
      <div className="flex flex-col gap-3" style={{ animation: 'fadeUp 0.3s ease' }}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <button onClick={() => selectWorkflow(null)} className="flex items-center gap-1.5 text-xs text-[#475569] hover:text-[#1e293b] w-fit">
            <ArrowLeft size={13} /> Back
          </button>
          <StageSaveBar
            journeyId={journeyId}
            buildFields={() => ({ workflowConfig: workflows.find((w) => w.id === selectedWorkflowId) ?? null })}
            onSaved={onJourneySaved}
          />
        </div>
        <WorkflowEditor workflowId={selectedWorkflowId} />
        {nextStageLabel && onAdvance && <StageCompleteNav nextLabel={nextStageLabel} onNext={onAdvance} />}
      </div>
    )
  }

  // ── Recipe editor view (v1) ──
  if (selectedRecipeId) {
    return (
      <div className="flex flex-col gap-3" style={{ animation: 'fadeUp 0.3s ease' }}>
        <button onClick={() => selectRecipe(null)} className="flex items-center gap-1.5 text-xs text-[#475569] hover:text-[#1e293b] w-fit">
          <ArrowLeft size={13} /> Back
        </button>
        <RecipeEditor recipeId={selectedRecipeId} />
        {nextStageLabel && onAdvance && <StageCompleteNav nextLabel={nextStageLabel} onNext={onAdvance} />}
      </div>
    )
  }

  // ── List / Guided entry view ──
  const workflowSuggestion = aiSuggestions?.workflow
  const recommendedRecipe = workflowSuggestion?.recipeKey
    ? V1_RECIPES.find((r) => r.key === workflowSuggestion.recipeKey)
    : undefined

  return (
    <div className="flex flex-col gap-6" style={{ animation: 'fadeUp 0.3s ease' }}>
      <ExperienceModeSwitcher mode={mode} onChange={onExperienceModeChange} />

      {workflowAi.visible && workflowSuggestion && (
        recommendedRecipe ? (
          <div className="space-y-3">
            <AiSuggestionBanner
              rationale={workflowSuggestion.rationale}
              confidence={workflowSuggestion.confidence}
              applied={workflowAi.applied}
              onApply={workflowAi.apply}
              onDismiss={workflowAi.dismiss}
            />
            {workflowAi.applied && appliedRecipeKey === workflowSuggestion.recipeKey && (
              <div className="rounded-[14px] p-4" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
                <V1RecipeVisual recipe={recommendedRecipe} workflowName="" notes="" />
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-[14px] p-3 flex items-start gap-2" style={{ background: '#f8fafc', border: '1px dashed #e5e7eb' }}>
            <Sparkles size={13} className="text-[#94a3b8] shrink-0 mt-0.5" />
            <p className="text-[11px] text-[#64748b] leading-snug">{workflowSuggestion.rationale}</p>
          </div>
        )
      )}

      {hasAnyAcceptedWorkflowStage(acceptedStages) && (
        <div className="space-y-2">
          <div>
            <h2 className="text-sm font-semibold text-[#1e293b]">Built from Accepted AI Configure Suggestions</h2>
            <p className="text-[11px] text-[#475569] mt-0.5">
              Reflects what was accepted in AI Configure on Account Setup. Accept or dismiss more stages there and this updates automatically.
            </p>
          </div>
          <WorkflowGraphPreview tasks={buildWorkflowTasksFromAcceptedStages(acceptedStages, aiSuggestions)} />
        </div>
      )}

      {mode === 'guided' ? (
        <WorkflowBuilderGuidedView accountId={accountId} onWorkflowCreated={handleWorkflowCreatedFromGuided} />
      ) : (
      <>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-sm font-semibold text-[#1e293b]">Workflows <span className="text-[10px] font-normal text-[#64748b]">(for v2 customers)</span></h2>
            <p className="text-[11px] text-[#475569] mt-0.5">Pick a workflow to open the visual editor, or add a new one.</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleNew()}
              placeholder="New workflow name…"
              className="bg-[#ffffff] border border-[#e5e7eb] rounded-md px-2.5 py-1.5 text-xs text-[#1e293b] placeholder:text-[#64748b] focus:outline-none focus:border-[#6366f1]/60 w-48"
            />
            <button onClick={handleNew} className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium bg-[#6366f1] text-white hover:bg-[#4f46e5] transition-colors">
              <Plus size={12} /> New
            </button>
            <button onClick={() => { setImportError(null); fileInputRef.current?.click() }} className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium text-[#334155] bg-white border border-[#cbd5e1] hover:bg-[#f1f5f9] transition-colors">
              <Upload size={12} /> Import JSON
            </button>
            <input ref={fileInputRef} type="file" accept="application/json" className="hidden" onChange={handleFileChange} />
          </div>
        </div>

        {importError && <p className="text-xs text-red-700">{importError}</p>}

        {workflows.length === 0 ? (
          <div className="text-xs text-[#64748b] border border-dashed border-[#cbd5e1] rounded-lg p-6 text-center">
            No workflows yet — create or import one above.
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {workflows.map((w) => {
              const isConfirming = confirmDeleteId === w.id
              return (
                <div
                  key={w.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSelectWorkflow(w)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSelectWorkflow(w) }}
                  className="flex items-start gap-3 rounded-lg border border-[#e5e7eb] bg-[#ffffff] px-4 py-3.5 hover:bg-[#f8fafc] hover:border-[#6366f1]/40 transition-colors cursor-pointer"
                >
                  <div className="flex-1 min-w-0">
                    <WorkflowPreviewStrip workflow={w} />
                  </div>
                  {isConfirming ? (
                    <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                      <span className="text-[10px] text-[#64748b] mr-1">Delete?</span>
                      <button
                        onClick={() => { deleteWorkflow(w.id); setConfirmDeleteId(null) }}
                        className="p-1.5 rounded text-red-700 hover:bg-red-500/10"
                        title="Confirm delete"
                      >
                        <Check size={13} />
                      </button>
                      <button
                        onClick={() => setConfirmDeleteId(null)}
                        className="p-1.5 rounded text-[#64748b] hover:bg-[#e5e7eb]"
                        title="Cancel"
                      >
                        <X size={13} />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(w.id) }}
                      className="p-1.5 rounded text-[#64748b] hover:text-red-700 hover:bg-[#e5e7eb] shrink-0"
                      title="Delete workflow"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Recipes (v1 clients) */}
      <div className="flex flex-col gap-3">
        <div>
          <h2 className="text-sm font-semibold text-[#1e293b]">Recipes <span className="text-[10px] font-normal text-[#64748b]">(for v1 customers)</span> <span className="text-[10px] font-normal text-amber-700">(not finished)</span></h2>
          <p className="text-[11px] text-[#475569] mt-0.5">Verification profiles (entity_profiles.json). Open one to configure its checks.</p>
        </div>
        <div className="flex flex-col gap-2">
          {recipes.map((r) => (
            <div
              key={r.id}
              role="button"
              tabIndex={0}
              onClick={() => selectRecipe(r.id)}
              onKeyDown={(e) => { if (e.key === 'Enter') selectRecipe(r.id) }}
              className="flex items-center gap-3 rounded-lg border border-[#e5e7eb] bg-[#ffffff] px-4 py-3 hover:bg-[#f8fafc] hover:border-[#6366f1]/40 transition-colors cursor-pointer"
            >
              <FileText size={15} className="text-[#4f46e5] shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-[#1e293b] truncate">{r.longName}</span>
                  <span className="font-mono text-[10px] text-[#64748b] shrink-0">{r.name}</span>
                </div>
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  {r.checkType.map((c) => (
                    <span key={c} className="text-[9px] px-1.5 py-0.5 rounded bg-[#e5e7eb] text-[#64748b] font-mono">{c}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      </>
      )}
    </div>
  )
}
